// Erros do checkout do Reservei → o que a tela faz com cada um.
//
// O servidor guarda UMA tentativa de compra por passe do Reservei (plano, ciclo,
// forma de pagamento, CPF/CNPJ, e-mail e cupom). Por isso a tela não pode tratar
// todo erro como "tente de novo": resultado incerto pode virar cobrança dupla, e
// combinação trocada nunca passa. Cada `kind` abaixo tem um desfecho próprio.

export type CheckoutErrorKind =
  /** Sem passe do Reservei, vencido ou já usado — recomeçar pelo app. */
  | 'session'
  /** Plano inexistente ou desativado. */
  | 'plan'
  /** Já tem plano ativo no Reservei — troca é pelo app. */
  | 'subscribed'
  /** Resultado incerto do provedor — NÃO deixar pagar de novo. */
  | 'unknown'
  /** Mudou plano/ciclo/forma/CPF/e-mail/cupom depois de começar a pagar. */
  | 'restart'
  /** Provedor recusou o pagamento (cartão) — dá pra tentar outro cartão. */
  | 'declined'
  /** Cupom recusado no envio. */
  | 'coupon'
  /** Dados recusados pela validação do servidor. */
  | 'invalid'
  /** Sem resposta (rede/timeout) — pode tentar de novo (o servidor é idempotente). */
  | 'network'
  | 'generic';

export interface CheckoutErrorInfo {
  kind: CheckoutErrorKind;
  message: string;
}

interface ErrorShape {
  response?: {
    status?: number;
    data?: { error?: { code?: unknown; message?: unknown }; message?: unknown };
  };
}

const SESSION_CODES = new Set(['TOKEN_INVALID', 'TOKEN_EXPIRED', 'TOKEN_ALREADY_USED', 'CLIENT_NOT_FOUND']);
const SUBSCRIBED_CODES = new Set(['SUBSCRIPTION_ALREADY_ACTIVE', 'OTHER_PLAN_ACTIVE_SAME_SAAS']);
const RESTART_CODES = new Set(['CHECKOUT_ATTEMPT_CONFLICT', 'CHECKOUT_ATTEMPT_NOT_RETRYABLE']);

export function classifyCheckoutError(err: unknown): CheckoutErrorInfo {
  const response = (err as ErrorShape | null | undefined)?.response;
  if (!response) {
    return {
      kind: 'network',
      message: 'Não conseguimos falar com o servidor. Confira sua internet e tente de novo.',
    };
  }

  const status = typeof response.status === 'number' ? response.status : undefined;
  const body = response.data?.error;
  const code = typeof body?.code === 'string' ? body.code : '';
  const serverMessage = typeof body?.message === 'string' ? body.message : '';

  if (SESSION_CODES.has(code) || status === 401) {
    return {
      kind: 'session',
      message: 'Sua sessão de compra expirou. Volte ao Reservei e clique em Assinar de novo. Nada foi cobrado.',
    };
  }
  if (code === 'CHECKOUT_RESULT_UNKNOWN') {
    return {
      kind: 'unknown',
      message:
        'Estamos confirmando o seu pagamento com o banco. Não pague de novo: assim que confirmar, seu plano é liberado no Reservei.',
    };
  }
  if (SUBSCRIBED_CODES.has(code)) {
    return {
      kind: 'subscribed',
      message: 'Você já tem um plano ativo no Reservei. Para trocar de plano, use o próprio Reservei.',
    };
  }
  if (RESTART_CODES.has(code)) {
    return {
      kind: 'restart',
      message:
        'Os dados da compra mudaram depois que o pagamento começou. Volte ao Reservei e clique em Assinar de novo. Nada a mais foi cobrado.',
    };
  }
  if (code === 'PLAN_NOT_FOUND' || status === 404) {
    return { kind: 'plan', message: 'Este plano não está disponível agora.' };
  }
  // Texto fixo de propósito: o servidor do Reservei não repassa o motivo do
  // provedor. Desde 27/10 a recusa confirmada libera trocar de cartão OU ir pro
  // Pix na mesma tela (o servidor aceita outra forma depois de `failed_known`).
  if (code === 'ASAAS_PAYMENT_FAILED') {
    return {
      kind: 'declined',
      message:
        'O pagamento não foi aprovado. Tente outro cartão ou pague no Pix — dá pra trocar aqui mesmo. Nada foi cobrado.',
    };
  }
  if (code === 'COUPON_INVALID') {
    return { kind: 'coupon', message: serverMessage || 'Este cupom não vale para esta compra.' };
  }
  // CPF/CNPJ que não serve para esta conta (já é de outro cadastro, ou a conta
  // já tem outro). O servidor desfaz a tentativa inteira — nada fica gravado —,
  // então o formulário NÃO trava: a pessoa corrige o documento e envia de novo.
  // Como "generic" ele travava os campos e o "tente de novo" falhava sempre.
  if (code === 'DOCUMENT_IDENTITY_CONFLICT') {
    return {
      kind: 'invalid',
      message:
        serverMessage || 'Esse CPF/CNPJ não pode ser usado nesta conta. Confira o número ou fale com o suporte do Reservei.',
    };
  }
  if (status === 400) {
    return {
      kind: 'invalid',
      message: serverMessage || 'Confira os dados preenchidos e tente de novo.',
    };
  }
  return {
    kind: 'generic',
    message: 'Não conseguimos concluir agora. Tente de novo em instantes ou fale com o suporte.',
  };
}
